import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { logEvent } from "@/lib/analytics";
import { IMAGES } from "@/lib/products";
import {
  Building2, Users, Palette, Award, Package, Truck, HeartHandshake,
  ChevronRight, ArrowRight, Check,
} from "lucide-react";

const TIERS = [
  { qty: "25 - 99", save: "10% off", perks: ["Logo on inner page", "Cream / Sand / Cocoa cover", "5-day dispatch"] },
  { qty: "100 - 499", save: "18% off", perks: ["Logo-ready planner gifting", "Custom color palette", "Dedicated account support", "Sample kit shipped"], featured: true },
  { qty: "500+", save: "Custom quote", perks: ["Custom cover, layout, sections", "Branded packaging", "Priority production", "Pan India delivery"] },
];

const USE_CASES = [
  { icon: Building2, t: "Corporates & Enterprises", d: "Team gifting, onboarding kits, leadership programs." },
  { icon: Users, t: "Founders & Startups", d: "Client giveaways, investor gifts, launch kits." },
  { icon: Award, t: "Universities & MBA Programs", d: "Cohort planners, incoming-batch welcome kits." },
  { icon: Palette, t: "Design Agencies & Studios", d: "Co-branded planners with your visual identity." },
];

const initialForm = {
  company: "",
  name: "",
  email: "",
  phone: "",
  quantity: "100 - 499",
  industry: "",
  timeline: "1-4 weeks",
  requirements: "",
};

export default function Bulk() {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const submit = async (event) => {
    event.preventDefault();
    if (!form.company || !form.email || !form.name) return toast.error("Please complete required fields");
    setSending(true);
    try {
      await addDoc(collection(db, "bulk_inquiries"), {
        ...form,
        createdAt: serverTimestamp(),
        status: "new",
      });
      logEvent("bulk_inquiry_submitted", { quantity: form.quantity });
      toast.success("Inquiry received - we'll reply within 24 hours.");
      setForm(initialForm);
    } catch (error) {
      console.error(error);
      toast.error("Could not send inquiry. Please email our team directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="bulk-page">
      <section className="bg-ivory border-b border-line">
        <div className="container-nc py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="flex items-center gap-2 text-xs text-muted_brown mb-6 uppercase tracking-[0.2em]">
              <Link to="/" className="hover:text-espresso">Home</Link>
              <ChevronRight size={12} />
              <span className="text-espresso">Bulk &amp; Corporate</span>
            </div>
            <p className="overline mb-4">For teams. For scale. For legacy.</p>
            <h1 className="font-serif text-5xl md:text-6xl text-espresso leading-[1.05]">
              Custom planners,
              <br /><span className="italic">crafted for your team.</span>
            </h1>
            <p className="mt-6 text-muted_brown leading-relaxed max-w-lg">
              A thoughtful planner is a gift that shows up on someone&rsquo;s desk every day for a
              year. Note Console partners with founders, corporates, and universities to
              create planner gifting kits for teams, cohorts, and professional milestones.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#inquiry" data-testid="bulk-cta" className="btn-primary">Request a quote <ArrowRight size={16} /></a>
              <a href="mailto:your.noteconsole@gmail.com" className="btn-outline">Email our team</a>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-6 text-xs text-muted_brown">
              <div className="flex items-center gap-2"><Check size={14} className="text-gold" /> Gifting available</div>
              <div className="flex items-center gap-2"><Check size={14} className="text-gold" /> 1-4 week lead time</div>
            </div>
          </div>
          <img src="/product/bulk-corporate-planners.jpg" alt="Bulk planner gifting in an office" className="rounded-2xl w-full aspect-[5/4] object-cover shadow-medium" loading="eager" fetchPriority="high" />
        </div>
      </section>

      <section className="container-nc py-20">
        <div className="max-w-2xl">
          <p className="overline mb-4">Who we serve</p>
          <h2 className="font-serif text-4xl md:text-5xl text-espresso leading-tight">
            One planner. Endless intentions.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
          {USE_CASES.map(({ icon: Icon, t, d }) => (
            <div key={t} className="bg-cream border border-line rounded-xl p-6">
              <div className="w-10 h-10 rounded-full gold-border flex items-center justify-center mb-4">
                <Icon size={16} strokeWidth={1.5} className="text-gold" />
              </div>
              <h3 className="font-serif text-lg text-espresso">{t}</h3>
              <p className="text-sm text-muted_brown mt-2 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-ivory border-y border-line">
        <div className="container-nc py-20 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <img src={IMAGES.planner_flatlay} alt="Planner gifting customization" className="rounded-2xl w-full aspect-[5/4] object-cover" />
          <div>
            <p className="overline mb-4">What you can customize</p>
            <h2 className="font-serif text-4xl md:text-5xl text-espresso leading-tight">
              Every detail. Yours.
            </h2>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3 text-sm text-espresso">
              {[
                "Logo-ready planner gifting",
                "Custom cover colour",
                "Branded first & last pages",
                "Custom section headers",
                "Founder / CEO note",
                "Company values page",
                "Team calendar & holidays",
                "Bespoke packaging",
                "Personalized name plate",
                "Pan India delivery",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 pb-2 border-b border-line/70">
                  <Check size={14} className="text-gold mt-1 shrink-0" /> {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="container-nc py-20">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="overline mb-4">Volume pricing</p>
          <h2 className="font-serif text-4xl md:text-5xl text-espresso">The more you order, the more they save.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {TIERS.map((tier) => (
            <div
              key={tier.qty}
              data-testid={`tier-${tier.qty.replace(/\s|-|\+/g, "")}`}
              className={`rounded-2xl p-8 ${
                tier.featured
                  ? "bg-espresso text-cream border border-espresso"
                  : "bg-cream text-espresso border border-line"
              }`}
            >
              <div className={`overline mb-3 ${tier.featured ? "text-champagne" : "text-muted_brown"}`}>{tier.qty} units</div>
              <div className={`font-serif text-4xl ${tier.featured ? "text-cream" : "text-espresso"}`}>{tier.save}</div>
              <ul className="mt-6 space-y-3 text-sm">
                {tier.perks.map((perk) => (
                  <li key={perk} className="flex items-start gap-2">
                    <Check size={14} className="mt-0.5 shrink-0 text-gold" /> {perk}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-champagne/40 border-y border-line">
        <div className="container-nc py-20">
          <div className="text-center mb-14">
            <p className="overline mb-4">How it works</p>
            <h2 className="font-serif text-4xl md:text-5xl text-espresso">From brief to boxes at your door.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { icon: HeartHandshake, t: "1. Brief", d: "Share your team size, timeline, and vision." },
              { icon: Palette, t: "2. Design", d: "We propose gifting options and mockups within 3 days." },
              { icon: Package, t: "3. Produce", d: "Sign-off unlocks production - 1-4 weeks depending on volume." },
              { icon: Truck, t: "4. Deliver", d: "Gift-packed or bulk-packed to your address." },
            ].map(({ icon: Icon, t, d }) => (
              <div key={t} className="bg-cream border border-line rounded-xl p-6">
                <Icon size={22} strokeWidth={1.5} className="text-gold mb-4" />
                <h4 className="font-serif text-lg text-espresso">{t}</h4>
                <p className="text-sm text-muted_brown mt-2 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="inquiry" className="container-nc py-20">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <p className="overline mb-4">Request a quote</p>
            <h2 className="font-serif text-4xl md:text-5xl text-espresso">Let&rsquo;s design your team&rsquo;s year.</h2>
            <p className="text-muted_brown mt-4 text-sm">We reply within 24 hours with a proposal and mockup.</p>
          </div>
          <form onSubmit={submit} noValidate className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-ivory border border-line rounded-2xl p-8">
            <input required placeholder="Company / Organization *" data-testid="bulk-company" className="input-nc bg-cream sm:col-span-2" value={form.company} onChange={(e) => update("company", e.target.value)} />
            <input required placeholder="Your name *" data-testid="bulk-name" className="input-nc bg-cream" value={form.name} onChange={(e) => update("name", e.target.value)} />
            <input required type="email" placeholder="Work email *" data-testid="bulk-email" className="input-nc bg-cream" value={form.email} onChange={(e) => update("email", e.target.value)} />
            <input placeholder="Phone (optional)" data-testid="bulk-phone" className="input-nc bg-cream" value={form.phone} onChange={(e) => update("phone", e.target.value)} />
            <select data-testid="bulk-quantity" value={form.quantity} onChange={(e) => update("quantity", e.target.value)} className="input-nc bg-cream">
              <option>10 - 24</option>
              <option>25 - 99</option>
              <option>100 - 499</option>
              <option>500 - 999</option>
              <option>1,000+</option>
            </select>
            <input placeholder="Industry (e.g. SaaS, MBA program)" data-testid="bulk-industry" className="input-nc bg-cream" value={form.industry} onChange={(e) => update("industry", e.target.value)} />
            <select data-testid="bulk-timeline" value={form.timeline} onChange={(e) => update("timeline", e.target.value)} className="input-nc bg-cream sm:col-span-2">
              <option>Under 3 weeks (rush)</option>
              <option>1-4 weeks</option>
              <option>4+ weeks</option>
              <option>Flexible</option>
            </select>
            <textarea rows={5} placeholder="Tell us about your vision, use case, or any gifting ideas..." data-testid="bulk-requirements" className="input-nc bg-cream sm:col-span-2" value={form.requirements} onChange={(e) => update("requirements", e.target.value)} />
            <button disabled={sending} data-testid="bulk-submit" className="btn-primary sm:col-span-2 disabled:opacity-60">
              {sending ? "Sending..." : "Send inquiry"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
